/* FLGNTLT — Produktvideos starten von allein, sobald sie im Blick sind.
 *
 * Dawn laedt Produktvideos bewusst erst auf Klick: Das <video> steckt in einem <template>,
 * der Besucher sieht bis dahin nur das Poster (Frame 0). Genau das war die Beschwerde —
 * die Kachel sieht aus wie ein Foto.
 *
 * Dieses Script nutzt die vorhandene Dawn-Mechanik statt sie zu ersetzen: Sobald eine
 * Video-Kachel zu 60 % sichtbar ist, wird `loadContent()` aufgerufen. Dawn schiebt das
 * <video> dann in den DOM und startet es selbst, weil das Template `autoplay muted` traegt.
 * Nichts wird vorgeladen, solange niemand hinsieht — die Datenmenge bleibt also dort, wo
 * Dawn sie haben wollte.
 *
 * Bewusste Ruecksichten:
 *  - `prefers-reduced-motion`: kein Selbststart. Dann traegt der (jetzt sichtbare) Knopf.
 *  - `saveData`: bei aktivem Datensparmodus ebenfalls kein Selbststart.
 *  - Blockiert der Browser den stummen Autostart trotzdem (z. B. iOS-Stromsparmodus),
 *    wird der Ladevorgang ZURUECKGEROLLT — sonst bliebe ein Standbild ohne Bedienelement
 *    stehen, also genau der Zustand, der behoben werden sollte.
 */
(function () {
  'use strict';

  var SICHTBAR_AB = 0.6;

  function darfSelbstStarten() {
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
      var netz = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (netz && netz.saveData) return false;
    } catch (e) { /* Feature nicht verfuegbar — dann eben starten */ }
    return true;
  }

  function istVideoKachel(dm) {
    var tpl = dm.querySelector('template');
    return !!(tpl && tpl.content && tpl.content.querySelector('video'));
  }

  /**
   * Waehlt die passende Fassung, BEVOR Dawn das Video einfuegt.
   *
   * Shopify transkodiert jedes Video in vier Fassungen (480p/720p/1080p + HLS), Dawns
   * `media_tag` gibt aber nur EINE aus — die groesste. Auf dem Handy lud der Browser damit
   * 1080p bei 7,2 Mbit/s komplett durch, bevor das erste Bild kam: gemessen rund 5 Sekunden
   * Stillstand auf der Kachel. Das Liquid gibt jetzt alle Fassungen aus, hier faellt die Wahl.
   *
   * Reihenfolge der Ueberlegung:
   *  1. HLS, wenn der Browser es kann (Safari/iOS) — adaptiv, beginnt mit einer kleinen
   *     Qualitaetsstufe und steigert sie, startet also praktisch sofort.
   *  2. Sonst MP4 nach Bildschirmbreite: auf dem Handy die kleinste Fassung (1,5 statt
   *     7,2 Mbit/s), auf dem Rechner die grosse.
   */
  /**
   * Laedt den HLS-Player genau einmal und nur dann, wenn er gebraucht wird.
   *
   * Shopify gibt im Storefront zwei Fassungen aus: HLS (adaptiv) und MP4 1080p mit
   * 7,2 Mbit/s. Safari spielt HLS von Haus aus — Chrome und Android nicht, dort bliebe
   * nur die grosse MP4, und genau die laesst die Kachel auf dem Handy sekundenlang stehen.
   * hls.js schliesst diese Luecke: rund 90 KB komprimiert gegen ein Video von zehn Megabyte.
   */
  var hlsLaeuft = null;
  function hlsHolen() {
    if (hlsLaeuft) return hlsLaeuft;
    var quelle = window.FLGNTLT_HLS_URL;
    if (!quelle) return Promise.resolve(null);
    hlsLaeuft = new Promise(function (fertig) {
      if (window.Hls) { fertig(window.Hls); return; }
      var s = document.createElement('script');
      s.src = quelle;
      s.async = true;
      s.onload = function () { fertig(window.Hls || null); };
      s.onerror = function () { fertig(null); }; // ohne Player faellt alles auf MP4 zurueck
      document.head.appendChild(s);
    });
    return hlsLaeuft;
  }

  function ueberHls(video, quelle, Hls, beiFehlschlag) {
    {
      var spieler = new Hls({
        startLevel: 0,       // kleinste Qualitaetsstufe zuerst → Bild kommt sofort
        maxBufferLength: 10, // nur zehn Sekunden vorhalten, nicht das ganze Video
        capLevelToPlayerSize: true,
      });
      spieler.on(Hls.Events.ERROR, function (_e, daten) {
        if (daten && daten.fatal) { try { spieler.destroy(); } catch (x) {} beiFehlschlag(); }
      });
      spieler.on(Hls.Events.MANIFEST_PARSED, function () {
        var versuch = video.play();
        if (versuch && versuch.catch) versuch.catch(function () { /* still */ });
      });
      spieler.loadSource(quelle);
      spieler.attachMedia(video);
      video.flgntltHls = spieler;
    }
  }

  function quelleWaehlen(video) {
    var quellen = Array.prototype.slice.call(video.querySelectorAll('source'));
    if (quellen.length < 2) return null; // nur eine Fassung — nichts zu entscheiden

    var hls = null;
    var mp4 = [];
    quellen.forEach(function (s) {
      var typ = s.getAttribute('type') || '';
      if (/mpegurl/i.test(typ)) hls = s;
      else if (/mp4/i.test(typ)) mp4.push(s);
    });
    mp4.sort(function (a, b) {
      return (Number(a.dataset.hoehe) || 0) - (Number(b.dataset.hoehe) || 0);
    });

    var hlsUrl = hls ? hls.getAttribute('src') : null;
    var schmal = window.innerWidth <= 749;
    var mp4Url = mp4.length ? (schmal ? mp4[0] : mp4[mp4.length - 1]).getAttribute('src') : null;

    // 🔴 `canPlayType('application/vnd.apple.mpegurl')` ist KEIN verlaesslicher Beweis:
    // Chrome antwortet "maybe" und kann HLS trotzdem nicht abspielen. Der Wert taugt nur
    // als Rueckfallebene, nachdem hls.js ausgeschlossen wurde.
    var nativMoeglich = !!(hlsUrl && video.canPlayType('application/vnd.apple.mpegurl'));

    // Solange offen ist, welcher Weg genommen wird, bekommt das <video> KEINE Quelle:
    // sonst begaenne der Browser sofort die grosse MP4 zu laden und wir wechseln mittendrin.
    if (!hlsUrl && mp4Url) video.setAttribute('src', mp4Url);

    return { hlsUrl: hlsUrl, mp4Url: mp4Url, nativMoeglich: nativMoeglich };
  }

  function zurueckrollen(dm) {
    var video = dm.querySelector('video');
    if (video) video.remove();
    dm.removeAttribute('loaded');
    dm.dataset.flgntltAutoplay = 'fehlgeschlagen';
  }

  function starten(dm) {
    if (dm.dataset.flgntltAutoplay) return;
    dm.dataset.flgntltAutoplay = 'laeuft';

    // Die Fassung wird im <template> gesetzt, also bevor Dawn das Element einfuegt und
    // von sich aus startet. Danach waere der Wechsel ein sichtbarer Neustart.
    var wahl = null;
    var tpl = dm.querySelector('template');
    if (tpl && tpl.content) {
      var vorlage = tpl.content.querySelector('video');
      if (vorlage) wahl = quelleWaehlen(vorlage);
    }

    if (typeof dm.loadContent === 'function') {
      dm.loadContent(false); // false = keinen Fokus setzen, sonst springt die Seite
    }

    var video = dm.querySelector('video');
    if (!video) { dm.dataset.flgntltAutoplay = ''; return; }

    video.muted = true;          // Pflicht fuer Autostart in allen Browsern
    video.loop = true;           // der Clip ist kurz; ein Standbild am Ende wirkt defekt
    video.setAttribute('playsinline', '');

    // 🔴 KEINE Bedienleiste waehrend des Selbststarts. iOS legt sonst Pause-Symbol,
    // ±10-Sekunden-Tasten, Fortschrittsbalken UND einen Abdunkel-Verlauf ueber das Bild —
    // die Kachel sah aus wie ein Videoplayer, nicht wie ein Produktfoto in Bewegung.
    // Die Steuerung erscheint erst, wenn jemand das Video antippt (siehe unten).
    video.controls = false;
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('controlslist', 'nodownload noplaybackrate');

    if (!video.dataset.flgntltTipp) {
      video.dataset.flgntltTipp = '1';
      // Ein Tipp holt die native Steuerung dazu — so bleibt der Ton erreichbar, ohne
      // dass die Leiste ungefragt ueber dem Bild liegt.
      video.addEventListener('click', function () {
        if (!video.controls) video.controls = true;
      });
    }

    var starteDirekt = function () {
      var versuch = video.play();
      if (versuch && typeof versuch.catch === 'function') {
        versuch.catch(function (fehler) {
          // 🔴 Nicht jeder abgelehnte play()-Aufruf ist ein verweigerter Autostart: Wer schnell an
          // der Kachel vorbeiscrollt, loest `anhalten()` aus, waehrend play() noch laeuft — das
          // ergibt einen AbortError. Wuerde man darauf zurueckrollen, verschwaende das Video.
          if (fehler && fehler.name === 'AbortError') return;
          zurueckrollen(dm);
        });
      }
    };

    var aufMp4 = function () {
      if (wahl && wahl.mp4Url) { video.setAttribute('src', wahl.mp4Url); video.load(); }
      starteDirekt();
    };

    if (wahl && wahl.hlsUrl) {
      // Reihenfolge nach Empfehlung von hls.js: erst der Player (er kann die kleinste
      // Qualitaetsstufe erzwingen und startet damit sofort), nativ nur als Rueckfall —
      // auf iPhones ohne MediaSource ist das der einzige Weg.
      hlsHolen().then(function (Hls) {
        if (Hls && Hls.isSupported()) {
          ueberHls(video, wahl.hlsUrl, Hls, aufMp4);
        } else if (wahl.nativMoeglich) {
          video.setAttribute('src', wahl.hlsUrl);
          video.load();
          starteDirekt();
        } else {
          aufMp4();
        }
      });
      return;
    }
    starteDirekt();
  }

  function anhalten(dm) {
    var video = dm.querySelector('video');
    if (video && !video.paused) video.pause();
  }

  function fortsetzen(dm) {
    var video = dm.querySelector('video');
    if (video && video.paused) {
      var versuch = video.play();
      if (versuch && typeof versuch.catch === 'function') versuch.catch(function () { /* still */ });
    }
  }

  function beobachten(wurzel) {
    var kacheln = (wurzel || document).querySelectorAll('deferred-media');
    if (!kacheln.length || !('IntersectionObserver' in window)) return;

    var selbststart = darfSelbstStarten();

    var beobachter = new IntersectionObserver(function (eintraege) {
      eintraege.forEach(function (eintrag) {
        var dm = eintrag.target;
        if (eintrag.isIntersecting) {
          if (!dm.dataset.flgntltAutoplay) starten(dm);
          else if (dm.dataset.flgntltAutoplay === 'laeuft') fortsetzen(dm);
        } else {
          anhalten(dm); // aus dem Blick = aus dem Sinn: spart Akku und Datenvolumen
        }
      });
    }, { threshold: SICHTBAR_AB });

    var hatVideo = false;
    Array.prototype.forEach.call(kacheln, function (dm) {
      if (!istVideoKachel(dm)) return;          // Modelle/iframes nicht anfassen
      hatVideo = true;
      if (dm.dataset.flgntltBeobachtet) return; // doppelte Beobachtung vermeiden
      dm.dataset.flgntltBeobachtet = '1';
      dm.classList.add('flgntlt-video');
      if (selbststart) beobachter.observe(dm);
    });

    // Den Player in einer ruhigen Minute holen, nicht erst wenn der Besucher zur Kachel
    // wischt — sonst kostet sein Nachladen genau die Wartezeit, die er sparen soll.
    // Nur auf Seiten mit Video, und nur wenn ohnehin selbst gestartet wird.
    if (hatVideo && selbststart) {
      if (window.requestIdleCallback) window.requestIdleCallback(hlsHolen, { timeout: 4000 });
      else setTimeout(hlsHolen, 1500);
    }
  }

  // ----------------------------------------------------------------- Startseiten-Slideshow
  /*
   * Zweiter Sammler fuer die Video-Slides der Startseite. Sie sind KEINE `deferred-media`-Kacheln:
   * `sections/slideshow.liquid` rendert je Slide zwei nackte <video>-Elemente (Desktop + Mobil),
   * von denen CSS eines ausblendet. Beide trugen bisher eine Quelle und `autoplay` — dadurch lud
   * die Startseite am 20.09.2026 alle acht Videos, obwohl eines sichtbar war.
   *
   * Seit dem Liquid-Patch tragen die Quellen `data-flgntlt-src` statt `src`. Wer eine Quelle
   * bekommt, entscheidet sich hier: nur was sichtbar ist und ins Bild kommt.
   */
  var SLIDE_AB = 0.25;

  function slideQuelleWaehlen(video) {
    var beste = null, hoechste = -1;
    var quellen = video.querySelectorAll('source[data-flgntlt-src]');
    for (var i = 0; i < quellen.length; i++) {
      var q = quellen[i];
      // Stufe 1 bewusst ohne HLS: kein Player-Download auf der Startseite, und die groesste
      // MP4 ist exakt die Datei, die heute schon geladen wird — der Hero bleibt unveraendert.
      if (!/mp4/i.test(q.getAttribute('type') || '')) continue;
      var h = Number(q.getAttribute('data-hoehe')) || 0;
      if (h > hoechste) { hoechste = h; beste = q.getAttribute('data-flgntlt-src'); }
    }
    return beste;
  }

  function slideStarten(video) {
    if (video.dataset.flgntltGestartet) { slideFortsetzen(video); return; }
    var quelle = slideQuelleWaehlen(video);
    if (!quelle) return;
    video.dataset.flgntltGestartet = '1';
    video.muted = true;                    // Pflicht fuer den Autostart
    // src setzen stoesst den Ladevorgang selbst an; ein load() danach wuerde den gerade
    // begonnenen Abruf abbrechen und neu starten.
    video.setAttribute('src', quelle);
    var versuch = video.play();
    if (versuch && typeof versuch.catch === 'function') {
      versuch.catch(function () { /* Autostart verweigert (z. B. iOS-Stromsparmodus): Poster bleibt */ });
    }
  }

  function slideFortsetzen(video) {
    if (!video.dataset.flgntltGestartet || !video.paused) return;
    var versuch = video.play();
    if (versuch && typeof versuch.catch === 'function') versuch.catch(function () { /* still */ });
  }

  /** Standbild nachreichen, bevor die Kachel ins Bild kommt — sonst waere sie kurz leer. */
  function slidePosterSetzen(video) {
    var p = video.getAttribute('data-flgntlt-poster');
    if (!p || video.getAttribute('poster')) return;
    video.setAttribute('poster', p);
  }

  function beobachteSlideshow(wurzel) {
    var videos = (wurzel || document).querySelectorAll('video[data-flgntlt-slide]');
    if (!videos.length || !('IntersectionObserver' in window)) return;
    if (!darfSelbstStarten()) return;      // reduzierte Bewegung / Datensparmodus: Poster bleibt

    // ZWEI Beobachter mit verschiedenen Wurzeln — bewusst nicht einer mit grossem rootMargin:
    // `intersectionRatio` bezieht sich auf die ERWEITERTE Wurzel, ein Video wuerde damit weit
    // vor der Sichtbarkeit die 25 % erreichen und zu frueh starten.
    var posterBeobachter = new IntersectionObserver(function (eintraege) {
      eintraege.forEach(function (eintrag) {
        if (!eintrag.isIntersecting) return;
        slidePosterSetzen(eintrag.target);
        posterBeobachter.unobserve(eintrag.target);   // einmal reicht
      });
    }, { rootMargin: '150% 0px', threshold: 0 });

    var videoBeobachter = new IntersectionObserver(function (eintraege) {
      eintraege.forEach(function (eintrag) {
        var v = eintrag.target;
        // 🔴 `isIntersecting` allein kippt schon bei einem einzigen Pixel. Der Erststart braucht
        // die echte Quote, sonst laedt auf Tablet-Hochkant die zweite Slideshow beim Aufruf mit.
        if (eintrag.isIntersecting && eintrag.intersectionRatio >= SLIDE_AB) { slidePosterSetzen(v); slideStarten(v); }
        else if (!eintrag.isIntersecting && !v.paused) v.pause();
      });
    }, { threshold: [0, SLIDE_AB] });

    Array.prototype.forEach.call(videos, function (v) {
      if (v.dataset.flgntltBeobachtet) return;
      v.dataset.flgntltBeobachtet = '1';
      posterBeobachter.observe(v);
      videoBeobachter.observe(v);
    });
  }

  /** Nach Rueckkehr aus dem Verlauf (bfcache) und nach Dawns pauseAllMedia() wieder anlaufen. */
  function slidesFortsetzen() {
    var videos = document.querySelectorAll('video[data-flgntlt-slide][data-flgntlt-gestartet]');
    Array.prototype.forEach.call(videos, function (v) {
      if (v.getBoundingClientRect().width > 0 && getComputedStyle(v).display !== 'none') slideFortsetzen(v);
    });
  }

  function alles(wurzel) { beobachten(wurzel); beobachteSlideshow(wurzel); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { alles(); });
  } else {
    alles();
  }

  // Der Theme-Editor und der Variantenwechsel rendern Abschnitte neu.
  document.addEventListener('shopify:section:load', function (e) { alles(e.target); });

  // Zurueck-Navigation aus dem bfcache; und Dawns Modal (Quick-Add) ruft beim Schliessen
  // pauseAllMedia() — danach stand bisher auch der Hero still.
  window.addEventListener('pageshow', function (e) { if (e.persisted) slidesFortsetzen(); });
  document.body && document.body.addEventListener('modalClosed', slidesFortsetzen);
})();
